import * as React from "react";
import {
  Button,
  Spinner,
  Body1,
  Caption1,
  Checkbox,
  Field,
  Textarea,
  tokens,
  makeStyles,
} from "@fluentui/react-components";
import { ArrowLeft24Regular, Send24Filled } from "@fluentui/react-icons";

import { TicketSearch } from "./TicketSearch";
import { TechnicianDropdown } from "./TechnicianDropdown";
import { GenericTaskBlock, GenericTaskValue, defaultTaskValue } from "./GenericTaskBlock";
import { AttachmentsBlock } from "./AttachmentsBlock";
import { ErrorBar } from "./ErrorBar";

import { getGlpiClient } from "../services/glpiClient";
import {
  getMailContext,
  getAttachmentBlob,
  MailContext,
} from "../services/outlookContext";
import {
  stripHtml,
  prepareMailHtmlForGlpi,
  buildTicketBodyContent,
} from "../services/htmlStripper";
import { buildTicketTaskPayloads } from "../services/ticketTaskPayload";
import { buildTicketWebUrl } from "../types/glpiConstants";
import { useTechnicians } from "../hooks/useTechnicians";

interface Props {
  onBack: () => void;
  onTasksAdded: (info: {
    ticketId: number;
    taskCount: number;
    url: string;
    attachmentsAttempted?: number;
    attachmentsFailed?: string[];
  }) => void;
  onError: (msg: string) => void;
}

const useStyles = makeStyles({
  root: {
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    gap: "8px",
    marginTop: "8px",
  },
  selectedTicket: {
    padding: "6px 8px",
    border: `1px solid ${tokens.colorBrandStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorBrandBackground2,
  },
  submitRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  htmlPreview: {
    maxHeight: "160px",
    overflowY: "auto",
    padding: "8px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    fontSize: tokens.fontSizeBase300,
  },
});

function buildMailTaskContent(
  mail: MailContext | null,
  useRichHtml: boolean,
  bodyHtml: string,
  bodyPlain: string
): string {
  if (!mail) return bodyPlain;
  return buildTicketBodyContent({
    useRichHtml,
    bodyHtml,
    bodyPlain,
    senderName: mail.senderName,
    senderEmail: mail.senderEmail,
  });
}

/** Met à jour le contenu (corps mail) de la première tâche dans le bloc. */
function applyMailBodyToFirstTask(
  prev: GenericTaskValue,
  mailContent: string
): GenericTaskValue {
  const rows = prev.tasks || [];
  if (rows.length === 0) return prev;
  return {
    ...prev,
    tasks: [{ ...rows[0], content: mailContent }, ...rows.slice(1)],
  };
}

export function AddTicketTaskForm({ onBack, onTasksAdded, onError }: Props): React.ReactElement {
  const styles = useStyles();

  const [mail, setMail] = React.useState<MailContext | null>(null);
  const [mailError, setMailError] = React.useState<string | null>(null);
  const [bodyHtml, setBodyHtml] = React.useState("");
  const [bodyPlain, setBodyPlain] = React.useState("");
  const [useRichBody, setUseRichBody] = React.useState(true);

  const [selectedTicketId, setSelectedTicketId] = React.useState<number | null>(null);
  const [selectedTicketTitle, setSelectedTicketTitle] = React.useState<string>("");
  const [ticketEntityId, setTicketEntityId] = React.useState<number | null>(null);
  const [technicianId, setTechnicianId] = React.useState<number | null>(null);
  const [task, setTask] = React.useState<GenericTaskValue>(() => defaultTaskValue(1));
  const [selectedAttachmentIds, setSelectedAttachmentIds] = React.useState<string[]>([]);

  const [submitting, setSubmitting] = React.useState(false);
  const [submitStep, setSubmitStep] = React.useState("");
  const [formError, setFormError] = React.useState<string | null>(null);

  const { technicians, loading: techLoading } = useTechnicians(true);

  const mailPreviewHtml = React.useMemo(
    () => buildMailTaskContent(mail, useRichBody, bodyHtml, bodyPlain),
    [mail, useRichBody, bodyHtml, bodyPlain]
  );

  // Lecture du mail : intitulé, corps de tâche, pièces jointes.
  React.useEffect(() => {
    let cancelled = false;
    getMailContext()
      .then(async (m) => {
        if (cancelled) return;
        setMail(m);
        const safeHtml = await prepareMailHtmlForGlpi(m.bodyHtml, m.attachments);
        if (cancelled) return;
        const plain = stripHtml(safeHtml || m.bodyText);
        setBodyPlain(plain);
        setBodyHtml(safeHtml);
        setUseRichBody(safeHtml.length > 40);

        const subject = (m.subject || "").trim() || "Mail importé";
        const mailContent = buildTicketBodyContent({
          useRichHtml: safeHtml.length > 40,
          bodyHtml: safeHtml,
          bodyPlain: plain,
          senderName: m.senderName,
          senderEmail: m.senderEmail,
        });

        setTask((prev) => {
          const rows = prev.tasks?.length ? prev.tasks : defaultTaskValue(1).tasks;
          const first = rows[0];
          return {
            ...prev,
            enabled: true,
            tasks: [
              {
                ...first,
                label: subject,
                content: mailContent,
              },
              ...rows.slice(1),
            ],
          };
        });

        const defaultSelected = m.attachments.filter((a) => !a.isInline).map((a) => a.id);
        setSelectedAttachmentIds(defaultSelected);
      })
      .catch((e: Error) => {
        if (!cancelled) setMailError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Quand l'utilisateur bascule HTML / texte, on met à jour le corps de la 1ère tâche.
  React.useEffect(() => {
    if (!mail) return;
    const mailContent = buildMailTaskContent(mail, useRichBody, bodyHtml, bodyPlain);
    setTask((prev) => applyMailBodyToFirstTask(prev, mailContent));
  }, [useRichBody, bodyHtml, bodyPlain, mail]);

  function handleTaskChange(next: GenericTaskValue): void {
    // Si l'utilisateur modifie le libellé de la 1ère ligne, on garde le corps mail synchronisé.
    const mailContent = buildMailTaskContent(mail, useRichBody, bodyHtml, bodyPlain);
    const rows = next.tasks || [];
    if (rows.length > 0 && mailContent) {
      setTask({
        ...next,
        tasks: [{ ...rows[0], content: mailContent }, ...rows.slice(1)],
      });
      return;
    }
    setTask(next);
  }

  function handleSelectTicket(id: number | null, title: string): void {
    setSelectedTicketId(id);
    setSelectedTicketTitle(title);
    setTicketEntityId(null);
    if (id == null) return;
    const client = getGlpiClient();
    if (!client) return;
    void client.fetchTicketEntitiesId(id).then((eid) => {
      if (eid != null) setTicketEntityId(eid);
    });
  }

  async function handleSubmit(): Promise<void> {
    setFormError(null);

    if (!selectedTicketId) {
      setFormError("Sélectionne d'abord un ticket dans la liste.");
      return;
    }
    if (technicianId === null) {
      setFormError("Indique la personne en charge de la tâche.");
      return;
    }

    const mailContent = buildMailTaskContent(mail, useRichBody, bodyHtml, bodyPlain);
    const taskWithBody = applyMailBodyToFirstTask({ ...task, enabled: true }, mailContent);

    const payloads = buildTicketTaskPayloads(selectedTicketId, taskWithBody, technicianId);
    if (payloads.length === 0) {
      setFormError("Ajoute au moins une tâche avec une date.");
      return;
    }
    if (!mailContent.trim()) {
      setFormError("Le corps du mail est vide — impossible de créer la tâche.");
      return;
    }

    const client = getGlpiClient();
    if (!client) {
      setFormError("Session GLPI absente.");
      return;
    }

    setSubmitting(true);
    try {
      for (let i = 0; i < payloads.length; i++) {
        setSubmitStep(`Création tâche ${i + 1}/${payloads.length}…`);
        await client.createTicketTask(selectedTicketId, payloads[i]);
      }

      const toUpload = (mail?.attachments ?? []).filter((a) =>
        selectedAttachmentIds.includes(a.id)
      );
      const attachmentsFailed: string[] = [];
      if (toUpload.length > 0) {
        await client.ensureSession();
        if (ticketEntityId == null) {
          const eid = await client.fetchTicketEntitiesId(selectedTicketId);
          if (eid != null) setTicketEntityId(eid);
        }
      }
      const uploadEntityId =
        ticketEntityId ?? (await client.fetchTicketEntitiesId(selectedTicketId));
      for (let i = 0; i < toUpload.length; i++) {
        const att = toUpload[i];
        setSubmitStep(`Upload pièce jointe ${i + 1}/${toUpload.length} : ${att.name}`);
        try {
          const fetched = await getAttachmentBlob(att.id, att);
          if (fetched.blob.size === 0) {
            throw new Error("Le fichier téléchargé depuis Outlook est vide (0 octet).");
          }
          await client.uploadDocumentToTicket(
            fetched.blob,
            fetched.name,
            selectedTicketId,
            {
              ticketTitle: selectedTicketTitle || mail?.subject,
              entitiesId: uploadEntityId ?? undefined,
            }
          );
        } catch (e) {
          const err = e as Error & { code?: string };
          const short =
            err.message && err.message.length < 200 && !err.message.includes("<!DOCTYPE")
              ? err.message
              : err.code === "GLPI_HTML_ERROR"
                ? "erreur serveur GLPI (voir console F12)"
                : err.code || "échec upload";
          attachmentsFailed.push(`${att.name} (${short})`);
          // eslint-disable-next-line no-console
          console.warn(`Upload PJ "${att.name}" échoué`, err.code ?? "", err.message, e);
        }
      }

      onTasksAdded({
        ticketId: selectedTicketId,
        taskCount: payloads.length,
        url: buildTicketWebUrl(client.apiUrl, selectedTicketId),
        attachmentsAttempted: toUpload.length,
        attachmentsFailed,
      });
    } catch (e) {
      const err = e as Error;
      onError(err.message);
      setFormError(err.message);
    } finally {
      setSubmitting(false);
      setSubmitStep("");
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={onBack} aria-label="Retour">
          Retour
        </Button>
        <Body1>
          <strong>Ajouter une tâche au ticket</strong>
        </Body1>
      </div>

      <ErrorBar message={mailError} title="Lecture mail" />
      <ErrorBar message={formError} title="Validation" />

      <TicketSearch selectedId={selectedTicketId} onSelect={handleSelectTicket} />

      {selectedTicketId && (
        <div className={styles.selectedTicket}>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Sélectionné :</Caption1>
          <Body1>
            <strong>#{selectedTicketId}</strong> {selectedTicketTitle}
          </Body1>
        </div>
      )}

      <TechnicianDropdown
        technicians={technicians}
        loading={techLoading}
        value={technicianId}
        onChange={setTechnicianId}
        label="Personne en charge de la tâche"
        required
      />

      <Field
        label="Contenu de la tâche (corps du mail)"
        hint="Envoyé dans GLPI comme description de la première tâche."
      >
        {bodyHtml.length > 0 && (
          <Checkbox
            label="Conserver la mise en page du mail (HTML)"
            checked={useRichBody}
            onChange={(_, d) => setUseRichBody(!!d.checked)}
            style={{ marginBottom: 8 }}
          />
        )}
        {useRichBody && bodyHtml ? (
          <div
            className={styles.htmlPreview}
            dangerouslySetInnerHTML={{ __html: mailPreviewHtml }}
          />
        ) : (
          <Textarea
            resize="vertical"
            rows={5}
            value={bodyPlain}
            onChange={(_, d) => setBodyPlain(d.value)}
          />
        )}
      </Field>

      <GenericTaskBlock
        ticketType={1}
        value={{ ...task, enabled: true }}
        onChange={handleTaskChange}
        showEnableToggle={false}
        sectionTitle="Planification (date et durée)"
      />

      {mail && mail.attachments.length > 0 && (
        <AttachmentsBlock
          attachments={mail.attachments}
          selected={selectedAttachmentIds}
          onChange={setSelectedAttachmentIds}
        />
      )}

      {submitting && submitStep && (
        <div className={styles.submitRow}>
          <Spinner size="tiny" />
          <span>{submitStep}</span>
        </div>
      )}

      <div className={styles.footer}>
        <Button onClick={onBack} disabled={submitting}>
          Annuler
        </Button>
        <Button
          appearance="primary"
          icon={submitting ? <Spinner size="tiny" /> : <Send24Filled />}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? "Envoi…" : "Ajouter au ticket"}
        </Button>
      </div>
    </div>
  );
}
